import { useTheme } from '@emotion/react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { numberFormatter } from '@dnhb/formatters';
import styled from '@emotion/styled';
import color from 'color';
import PropTypes from 'prop-types';

const GraphWrapper = styled.div`
  .highcharts-data-label:nth-of-type(odd) > span {
    z-index: 99;
  }
`;

const customPercentageFormatter = value => numberFormatter(value, 2, 2, '', '%');

const Graph = ({ output }) => {
  const { highlightColor, secondaryFontColor, primaryFontColor, fontFamily, borderColor } = useTheme();
  const startingPoint = output[0].interest_rate * 0.9;
  const data = output.map(rate => ({
    y: rate.interest_rate,
    label: rate.mortgage_provider_name,
  }));

  return (
    <GraphWrapper>
      <HighchartsReact
        highcharts={Highcharts}
        options={{
          chart: {
            type: 'bar',
            height: 260,
            backgroundColor: 'transparent',
            borderColor: 'red',
            marginLeft: 20,
            marginRight: 20,
            style: {
              fontFamily,
              color: primaryFontColor,
            },
          },
          title: { text: null },
          credits: { enabled: false },
          xAxis: {
            type: 'category',
            visible: false,
          },
          yAxis: {
            title: { text: null },
            min: startingPoint,
            gridLineColor: borderColor,
            gridLineDashStyle: 'dash',
            tickPixelInterval: 88,
            labels: {
              rotation: 0,
              style: { fontSize: '14px', color: primaryFontColor, textOverflow: 'clip' },
              formatter() {
                return customPercentageFormatter(this.value);
              },
            },
          },
          legend: {
            enabled: false,
          },
          tooltip: {
            enabled: false,
          },
          plotOptions: {
            bar: {
              groupPadding: 0,
              pointPadding: 0.08,
              borderWidth: 0,
              colorByPoint: true,
              colors: [
                color(highlightColor).lighten(0).hex().toString(),
                color(highlightColor).lighten(0.1).hex().toString(),
                color(highlightColor).lighten(0.2).hex().toString(),
                color(highlightColor).lighten(0.3).hex().toString(),
                color(highlightColor).lighten(0.4).hex().toString(),
              ],
              dataLabels: {
                enabled: true,
                inside: true,
                useHTML: true,
                allowOverlap: true,
                color: secondaryFontColor,
                style: {
                  fontSize: '14px',
                  fontWeight: '800',
                  textOutline: 0,
                },
              },
            },
          },
          series: [
            {
              name: 'Totale lening',
              type: 'bar',
              color: highlightColor,
              marker: { enabled: false, symbol: 'circle' },
              dataLabels: [
                {
                  align: 'right',
                  formatter() {
                    const backgroundColor = color(this.color).toString();
                    const transparentColor = color(this.color).alpha(0).toString();
                    return `
                        <span style="
                          padding-left: 16px;
                          background: linear-gradient(90deg, ${transparentColor} 0%, ${backgroundColor} 16%);
                        ">
                          ${customPercentageFormatter(this.y)}
                        </span>
                      `;
                  },
                },
                {
                  align: 'left',
                  style: {
                    fontSize: '12px',
                  },
                  formatter() {
                    return this.point.label;
                  },
                },
              ],
              data,
            },
          ],
        }}
      />
    </GraphWrapper>
  );
};

export default Graph;

Graph.propTypes = {
  output: PropTypes.arrayOf(
    PropTypes.shape({
      interest_rate: PropTypes.number,
      mortgage_provider_name: PropTypes.string,
    })
  ),
};
